import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ApolloError, useApolloClient, useMutation, useQuery } from '@apollo/client';
import { useSearchParams } from 'react-router-dom';
import dayjs from 'dayjs';
import { alpha, Alert, Box, Button, Chip, Typography } from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import HourglassBottomRoundedIcon from '@mui/icons-material/HourglassBottomRounded';
import SkipNextRoundedIcon from '@mui/icons-material/SkipNextRounded';
import StopCircleOutlinedIcon from '@mui/icons-material/StopCircleOutlined';

import { config } from '../../../config';
import { getDeviceId } from '../../../common/device';
import { useToast } from '../../../common/hooks/useToast';
import { getTokens } from '../../../common/manage_tokens';
import { describeError } from '../../../common/rateLimit';
import { Item } from '../../../styles/common';
import { CrmPageFrame } from '../CrmPageFrame';
import { buildFilter } from '../contacts/contactList';
import { displayName } from '../contactDetail/contactDetail';
import { dialledNumber } from '../contactDetail/edit/contactEdit';
import { describeLogged } from '../calls/logCall';
import { LogCallForm, LoggedCall } from '../calls/LogCallForm';
import { useCallTimer } from '../calls/useCallTimer';
import { CRM_CALL_QUEUE_NEXT, CRM_CALL_QUEUE_RELEASE, CRM_CALL_QUEUE_SUMMARY } from './callQueue.queries';
import {
  countLogged,
  countSkipped,
  describeTally,
  formatHold,
  HOLD_WARNING_SEC,
  holdLeft,
  NO_TALLY,
  parseQueueSettings,
  QueueItem,
  queueSearchParams,
  QueueSettings,
  QueueSummary,
  Tally,
} from './callQueue';
import { QueueHistory, QueuePerson } from './QueuePerson';
import { QueueSetup } from './QueueSetup';

type Stage =
  | { kind: 'setup' }
  /** `after`: the cursor that served this person, to hold them again once the hold runs out. */
  | { kind: 'calling'; item: QueueItem; after: string | null }
  | { kind: 'done' };

/** Typing in a field: S is a letter there, not Skip. */
const isTyping = (target: EventTarget | null) => {
  const el = target as HTMLElement | null;
  if (!el) return false;
  return el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.tagName === 'SELECT' || el.isContentEditable;
};

/**
 * The page is closing with a person held: free them now rather than in 15
 * minutes. `keepalive` lets the request outlive the page; if the browser
 * drops it anyway, the hold simply expires.
 */
const releaseOnLeave = () => {
  const token = getTokens();
  if (!token || typeof fetch !== 'function') return;
  try {
    fetch(config.apiUrl, {
      method: 'POST',
      keepalive: true,
      headers: {
        'content-type': 'application/json',
        authorization: `Bearer ${token}`,
        'x-device-id': getDeviceId(),
        'Apollo-Require-Preflight': 'true',
      },
      body: JSON.stringify({ operationName: 'CrmCallQueueRelease', query: 'mutation CrmCallQueueRelease { crmCallQueueRelease }' }),
    }).catch(() => undefined);
  } catch {
    // The hold expires by itself.
  }
};

/** How long the person stays held for this caller, ticking once a second on its own. */
const HoldChip = ({ claimExpiresAt, onHoldAgain, busy }: { claimExpiresAt: string | null; onHoldAgain: () => void; busy: boolean }) => {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const tick = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(tick);
  }, []);
  const left = holdLeft(claimExpiresAt, now);
  if (left > 0) {
    return (
      <Chip
        size="small"
        icon={<HourglassBottomRoundedIcon />}
        color={left <= HOLD_WARNING_SEC ? 'warning' : 'default'}
        variant="outlined"
        label={`Held for you · ${formatHold(left)}`}
        title="Other callers are not given this person until then, or until you log the call."
        sx={{ fontVariantNumeric: 'tabular-nums' }}
      />
    );
  }
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <Chip size="small" color="warning" icon={<HourglassBottomRoundedIcon />} label="Your hold ran out" />
      <Button size="small" onClick={onHoldAgain} disabled={busy}>
        Hold again
      </Button>
    </Box>
  );
};

/**
 * The call queue (card 21): a worklist over any contact filter, one person at
 * a time, on card 16's queue.
 *
 * - **Set up:** the contact list's filters, the order (follow-ups first, or
 *   only follow-ups), and a rest period; the summary says how many people that
 *   is and why the rest are left out. Everything is in the URL, so a queue can
 *   be shared as a link, and "Call these people" on Contacts opens one.
 * - **Calling:** the person's numbers (dialling one starts the call timer),
 *   why they were served, their last calls, and the log-a-call form beside.
 *   "Log and next" (Ctrl+Enter) logs and serves the next; Skip (S) moves on
 *   without logging. Each person is held for this caller (15 minutes on the
 *   API) so two callers never ring the same one.
 * - **Leaving** frees the person held: Stop, leaving the screen, or closing
 *   the tab.
 *
 * Requests: the filter choices and the summary while setting up; then per
 * person one `crmCallQueueNext`, their last calls, and `crmLogCall`. Nothing
 * polls.
 */
export const CrmCallQueue = () => {
  const [params, setParams] = useSearchParams();
  const settings = useMemo(() => parseQueueSettings(params), [params]);
  const filter = useMemo(() => buildFilter(settings.list), [settings.list]);
  const variables = { filter: filter ?? null, followUps: settings.mode, rest: settings.rest };

  const client = useApolloClient();
  const toast = useToast();
  const timer = useCallTimer();
  const submitRef = useRef<(() => void) | null>(null);

  const [stage, setStage] = useState<Stage>({ kind: 'setup' });
  const [tally, setTally] = useState<Tally>(NO_TALLY);
  const [startedWith, setStartedWith] = useState<number | null>(null);
  const [lastSession, setLastSession] = useState<{ tally: Tally; endedAt: number } | null>(null);
  const [lastLogged, setLastLogged] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const summaryQuery = useQuery<{ crmCallQueueSummary: QueueSummary }>(CRM_CALL_QUEUE_SUMMARY, {
    variables,
    skip: stage.kind === 'calling',
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
  });
  const summary = summaryQuery.data?.crmCallQueueSummary;

  const [next, { loading: serving }] = useMutation<{ crmCallQueueNext: QueueItem }>(CRM_CALL_QUEUE_NEXT);

  // Holding someone: freed on Stop, on leaving the screen, and on closing the tab.
  const holding = useRef(false);
  holding.current = stage.kind === 'calling';
  useEffect(() => {
    const onHide = () => {
      if (holding.current) releaseOnLeave();
    };
    window.addEventListener('pagehide', onHide);
    return () => {
      window.removeEventListener('pagehide', onHide);
      if (holding.current) client.mutate({ mutation: CRM_CALL_QUEUE_RELEASE }).catch(() => undefined);
    };
  }, [client]);

  /** Serves the first person past `after` (from the top without it). */
  const serve = useCallback(
    async (after: string | null, previousId?: number) => {
      setError(null);
      try {
        const { data } = await next({ variables: { ...variables, after } });
        const item = data?.crmCallQueueNext;
        if (!item) return;
        timer.reset();
        if (!item.contact) {
          setStage({ kind: 'done' });
          return;
        }
        setNotice(
          previousId != null && previousId !== item.contact.id
            ? 'The person you held was taken by another caller while your hold had run out. This is the next one.'
            : null,
        );
        setStage({ kind: 'calling', item, after });
      } catch (e) {
        setError(describeError(e, 'The next person could not be fetched'));
      }
    },
    // The variables are rebuilt from the URL each render; their JSON is what matters.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [next, timer.reset, JSON.stringify(variables)],
  );

  const changeSettings = (nextSettings: QueueSettings) => setParams(queueSearchParams(nextSettings), { replace: true });

  const start = async () => {
    setTally({ ...NO_TALLY, startedAt: Date.now() });
    setStartedWith(summary?.inQueue ?? null);
    setLastSession(null);
    setLastLogged(null);
    await serve(null);
  };

  const current = stage.kind === 'calling' ? stage : null;

  const skip = () => {
    if (!current || serving) return;
    setTally((t) => countSkipped(t));
    setLastLogged(null);
    serve(current.item.cursor);
  };

  const logged = (call: LoggedCall) => {
    if (!current) return;
    setTally((t) => countLogged(t, call.outcome as never));
    setLastLogged(`${displayName(current.item.contact!)}: ${describeLogged(call, dayjs()).replace(/^Logged: /, '')}`);
    serve(current.item.cursor);
  };

  const stop = async () => {
    setLastSession({ tally, endedAt: Date.now() });
    setStage({ kind: 'setup' });
    setNotice(null);
    timer.reset();
    try {
      await client.mutate({ mutation: CRM_CALL_QUEUE_RELEASE });
    } catch {
      // The hold expires by itself.
    }
  };

  // Keys while calling: S skips; Ctrl+Enter logs from anywhere on the screen.
  useEffect(() => {
    if (stage.kind !== 'calling') return undefined;
    const onKey = (event: KeyboardEvent) => {
      if (document.querySelector('[role="dialog"], [role="listbox"]')) return;
      if (event.key === 'Enter' && (event.ctrlKey || event.metaKey)) {
        event.preventDefault();
        submitRef.current?.();
        return;
      }
      if (isTyping(event.target) || event.ctrlKey || event.metaKey || event.altKey) return;
      if (event.key.toLowerCase() === 's') {
        event.preventDefault();
        skip();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  });

  const served = tally.logged + tally.skipped + (current ? 1 : 0);
  const started = tally.logged + tally.skipped > 0;

  return (
    <CrmPageFrame
      title="Call queue"
      description="Work through a list one call at a time. Do not call and do not contact are never served."
      actions={
        current ? (
          <Button variant="outlined" color="inherit" startIcon={<StopCircleOutlinedIcon />} onClick={stop}>
            Stop calling
          </Button>
        ) : undefined
      }
    >
      {stage.kind === 'setup' && (
        <QueueSetup
          settings={settings}
          onChange={changeSettings}
          summary={summary}
          summaryLoading={summaryQuery.loading}
          summaryError={summaryQuery.error as ApolloError | undefined}
          onStart={start}
          starting={serving}
          lastSession={lastSession}
          startError={error}
        />
      )}

      {current && current.item.contact && (
        <Box sx={{ display: 'grid', gap: 2 }}>
          <Item
            sx={{
              px: { xs: 1.5, sm: 2 },
              py: 1.25,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 1.5,
              flexWrap: 'wrap',
            }}
          >
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="body2" sx={{ fontWeight: 600 }} aria-live="polite">
                Person {served}
                {startedWith != null ? ` of about ${Math.max(startedWith, served).toLocaleString('en-US')}` : ''}
              </Typography>
              <Typography variant="caption" color="text.secondary" component="p">
                {started ? describeTally(tally, Date.now()) : 'The first call of this session.'}
                {lastLogged ? ` Last: ${lastLogged}` : ''}
              </Typography>
            </Box>
            <HoldChip
              claimExpiresAt={current.item.claimExpiresAt}
              busy={serving}
              onHoldAgain={() => serve(current.after, current.item.contact?.id)}
            />
          </Item>

          {notice && (
            <Alert severity="info" onClose={() => setNotice(null)}>
              {notice}
            </Alert>
          )}
          {error && <Alert severity="error">{error}</Alert>}

          {/* Wide: the person and their history on the left, the form beside both. A phone: the
              person, the form, then the history, so logging does not mean scrolling past it. */}
          <Box
            sx={{
              display: 'grid',
              gap: 2,
              gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: 'minmax(0, 1fr) minmax(360px, 460px)' },
              gridTemplateAreas: { xs: '"person" "form" "history"', md: '"person form" "history form"' },
              gridTemplateRows: { md: 'auto 1fr' },
              alignItems: 'start',
              opacity: serving ? 0.6 : 1,
              transition: 'opacity 120ms',
            }}
          >
            <Box sx={{ gridArea: 'person', minWidth: 0 }}>
              <QueuePerson
                key={current.item.contact.id}
                item={current.item}
                onDial={timer.start}
                onCopied={(text) => toast.info(`Copied ${text}`)}
              />
            </Box>
            <Box sx={{ gridArea: 'history', minWidth: 0 }}>
              <QueueHistory key={current.item.contact.id} item={current.item} />
            </Box>
            <Item sx={{ gridArea: 'form', p: { xs: 1.5, sm: 2 }, position: { md: 'sticky' }, top: { md: 0 }, minWidth: 0 }}>
              <Box component="section" aria-labelledby="crm-queue-log">
                <Typography id="crm-queue-log" variant="subtitle1" component="h2" sx={{ fontWeight: 600, mb: 1.5 }}>
                  Log this call
                </Typography>
                <LogCallForm
                  key={current.item.contact.id}
                  contactId={current.item.contact.id}
                  name={current.item.contact.firstName?.trim() || displayName(current.item.contact)}
                  blocked={current.item.contact.doNotContact ? 'doNotContact' : current.item.contact.doNotCall ? 'doNotCall' : null}
                  submitLabel="Log and next"
                  number={dialledNumber(current.item.contact)}
                  onLogged={logged}
                  timer={timer}
                  autoFocus
                  submitRef={submitRef}
                  disabled={serving}
                  secondary={
                    <Button onClick={skip} disabled={serving} startIcon={<SkipNextRoundedIcon />} title="Key: S">
                      Skip
                    </Button>
                  }
                />
                <Typography variant="caption" color="text.secondary" component="p" sx={{ mt: 1.5 }}>
                  Keys: 1–9 what happened · Ctrl+Enter log and next · S skip
                </Typography>
              </Box>
            </Item>
          </Box>
        </Box>
      )}

      {stage.kind === 'done' && (
        <Item sx={{ py: 6, px: 2, display: 'grid', justifyItems: 'center', gap: 1, textAlign: 'center' }}>
          <Box
            sx={(theme) => ({
              width: 56,
              height: 56,
              borderRadius: '50%',
              display: 'grid',
              placeItems: 'center',
              bgcolor: alpha(theme.palette.success.main, 0.1),
              color: 'success.main',
              mb: 1,
            })}
          >
            <CheckCircleOutlineIcon />
          </Box>
          <Typography variant="h6" component="p">
            Nobody left in this queue
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 460 }}>
            {tally.startedAt ? describeTally(tally, Date.now()) : ''}{' '}
            {tally.skipped
              ? `Starting again from the top brings back the ${tally.skipped === 1 ? 'person' : `${tally.skipped} people`} you skipped, unless someone else has called them.`
              : 'Everyone this filter holds has been served.'}
          </Typography>
          {error && <Alert severity="error">{error}</Alert>}
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', justifyContent: 'center', mt: 1 }}>
            <Button variant="contained" onClick={() => serve(null)} disabled={serving}>
              Start again from the top
            </Button>
            <Button
              variant="outlined"
              onClick={() => {
                setLastSession(tally.startedAt ? { tally, endedAt: Date.now() } : null);
                setStage({ kind: 'setup' });
              }}
            >
              Change who to call
            </Button>
          </Box>
        </Item>
      )}
    </CrmPageFrame>
  );
};
